import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { ContractService, ContractRequest } from "./contract.service";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import ContractForm from "./contractForm";
import { ContractFormDataProvider } from "./contractFormDataContext";
import male from "../../../../src/assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { useTenantChange } from "../../../hooks/useTenantChange";
import ExportComponent from "../../../components/ExportComponent";
import CollapseHeader from "../../../core/common/collapse-header";
import { Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import * as bootstrap from "bootstrap";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import { getStageColor } from "../../../utils/stageColors";
import { sortRecords } from "../../../utils/sortUtils";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import Table from "../../../core/common/dataTable/index";
import ManageColumns from "../../../components/filters/ManageColumns";
import ContractsGrid from "./contractsGrid";
import EntityDetailsModal, {
  EntityDetailSection,
  EntityDetailLink,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { openOffcanvas } from "../../../utils/offcanvasUtils";
import {
  getLifecycleTypeName,
  getLifecycleTypePluralName,
} from "../../../utils/lifecycleUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

interface ContractsProps {
  onViewSwitch?: (view: "list" | "grid") => void;
}

const Contracts = ({ onViewSwitch }: ContractsProps) => {
  const [currentView, setCurrentView] = useState<"list" | "grid">("list");
  const navigate = useNavigate();

  const handleViewSwitch = useCallback(
    (view: "list" | "grid") => {
      setCurrentView(view);
      onViewSwitch?.(view);
    },
    [onViewSwitch]
  );
  const [contracts, setContracts] = useState<any[]>([]);
  const [contractsCount, setContractsCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { selectedTenantId } = useContext(AuthContext);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
  });

  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  const [addContractLoading, setAddContractLoading] = useState(false);
  const [addContractError, setAddContractError] = useState<string | null>(null);
  const [selectedContract, setSelectedContract] = useState<any | null>(null);
  const [detailsContract, setDetailsContract] = useState<any | null>(null);
  const [isAddContractOpen, setIsAddContractOpen] = useState(false);
  const [editContractLoading, setEditContractLoading] = useState(false);
  const [editContractError, setEditContractError] = useState<string | null>(
    null
  );
  const [selectedContractForDelete, setSelectedContractForDelete] = useState<
    any | null
  >(null);
  const [deleteContractLoading, setDeleteContractLoading] = useState(false);
  const [deleteContractError, setDeleteContractError] = useState<string | null>(
    null
  );
  const [resetFormTrigger, setResetFormTrigger] = useState(0);
  const [stars, setStars] = useState<{ [key: number]: boolean }>({});
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const defaultColumnVisibility = {
    subject: true,
    contract_type: true,
    amount: true,
    assigned_team: false,
    assigned_user: true,
    created_datetime: true,
    related_proposal: false,
    currency_code: false,
    company: true,
  };

  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(() => {
    try {
      const saved = localStorage.getItem("contracts_column_visibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        const visibility: { [key: string]: boolean } = {
          ...defaultColumnVisibility,
        };
        Object.keys(parsed).forEach((key) => {
          if (key in defaultColumnVisibility) {
            visibility[key] = parsed[key] === true;
          }
        });
        const visibleCount = Object.values(visibility).filter(Boolean).length;
        if (visibleCount === 0) {
          const defaultKeys = Object.keys(defaultColumnVisibility);
          for (const key of defaultKeys) {
            if (
              defaultColumnVisibility[
              key as keyof typeof defaultColumnVisibility
              ]
            ) {
              visibility[key] = true;
              break;
            }
          }
        }
        return visibility;
      }
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
    }
    return defaultColumnVisibility;
  });

  const saveColumnVisibilityToStorage = useCallback(
    (visibility: { [key: string]: boolean }) => {
      try {
        localStorage.setItem(
          "contracts_column_visibility",
          JSON.stringify(visibility)
        );
      } catch (error) {
        console.error(
          "Error saving column visibility to localStorage:",
          error
        );
      }
    },
    []
  );

  const availableColumns = [
    { key: "subject", label: "Subject" },
    { key: "company", label: "Company" },
    { key: "contract_type", label: `${getLifecycleTypeName("CONTRACT")} Type` },
    { key: "amount", label: "Amount" },
    { key: "assigned_user", label: "Assigned User" },
    { key: "created_datetime", label: "Created" },
    { key: "assigned_team", label: "Assigned Team" },
    { key: "related_proposal", label: `Related ${getLifecycleTypeName("PROPOSAL")}` },
    { key: "currency_code", label: "Currency" },

  ];

  const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
    const storageKey = `${window.location.pathname}_column_order`;
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderKeys = JSON.parse(savedOrder);
        return [...availableColumns].sort((a, b) => {
          const indexA = orderKeys.indexOf(a.key);
          const indexB = orderKeys.indexOf(b.key);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } catch (e) {
        return [...availableColumns];
      }
    }
    return [...availableColumns];
  });

  const getSortedAvailableColumns = () => {
    return orderedAvailableColumns;
  };

  const handleColumnVisibilityChange = (
    columnKey: string,
    isVisible: boolean
  ) => {
    const currentVisibleCount =
      Object.values(columnVisibility).filter(Boolean).length;
    if (isVisible && currentVisibleCount >= 6) {
      showErrorAlert(
        "Maximum 6 columns can be enabled at once. Please disable another column first."
      );
      return;
    }
    if (!isVisible && currentVisibleCount <= 1) {
      showErrorAlert("At least one column must remain visible.");
      return;
    }
    const newColumnVisibility = {
      ...columnVisibility,
      [columnKey]: isVisible,
    };
    setColumnVisibility(newColumnVisibility);
    saveColumnVisibilityToStorage(newColumnVisibility);
  };

  const handleResetColumns = () => {
    setColumnVisibility(defaultColumnVisibility);
    saveColumnVisibilityToStorage(defaultColumnVisibility);
  };

  const clearContractsData = () => {
    setContracts([]);
    setContractsCount(null);
    setError(null);
    setCurrentPage(1);
    setPageSize(12);
    setTotalPages(1);
  };

  useTenantChange({
    onTenantChange: () => {
      clearContractsData();
    },
    refreshData: () => {
      if (selectedTenantId) {
      }
    },
    clearData: clearContractsData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "subject",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false,
      pageName: "contract" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      const mergedFilters = mergeTableFilterChanges(filters, newFilters);
      setFilters(mergedFilters);
      setCurrentPage(1);
      fetchContracts(1, pageSize, mergedFilters);
    },
    [filters, pageSize]
  );

  const handleDropdownFilterChange = useCallback(
    (dropdownFilters: Record<string, any>) => {
      const updatedFilters = {
        ...filters,
        ...dropdownFilters,
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      fetchContracts(1, pageSize, updatedFilters);
    },
    [filters, pageSize]
  );

  const applyListFilters = useCallback(
    (nextFilters: FilterState) => {
      setFilters(nextFilters);
      setCurrentPage(1);
      fetchContracts(1, pageSize, nextFilters);
    },
    [pageSize]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const getContractsApiUrl = (
    page: number = 1,
    limit: number = 12,
    filterParams: Record<string, any> = {}
  ) => {
    if (!selectedTenantId) return "";

    let url = `${API_BASE_URL}/contract/?tenant_id=${selectedTenantId}&page=${page}&limit=${limit}`;

    const archivedFilter = filterParams.archivedFilter || "no";
    if (archivedFilter === "yes") {
      url += `&is_archive=true`;
    } else if (archivedFilter === "no") {
      url += `&is_archive=false`;
    }

    Object.keys(filterParams).forEach((key) => {
      if (["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
        return;
      }
      if (filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== "") {
        url += `&${key}=${encodeURIComponent(filterParams[key])}`;
      }
    });

    if (filterParams.searchTerm) {
      url += `&subject=${encodeURIComponent(filterParams.searchTerm)}`;
    }

    if (filterParams.dateRange && filterParams.dateRange[0]) {
      url += `&created_datetime_gte=${filterParams.dateRange[0].toISOString().split("T")[0]
        }`;
    }

    if (filterParams.dateRange && filterParams.dateRange[1]) {
      url += `&created_datetime_lte=${filterParams.dateRange[1].toISOString().split("T")[0]
        }`;
    }

    if (filterParams.statusFilter) {
      url += `&contract_type_id=${encodeURIComponent(
        filterParams.statusFilter
      )}`;
    }

    const authRaw = localStorage.getItem("authResponse");
    if (!authRaw) return url;

    try {
      const auth = JSON.parse(authRaw);

      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find(
          (t: any) => String(t.tenant_id) === selectedTenantId
        )
        : null;

      if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
        url += `&sales_user_id=${auth.user_id}`;
      }
    } catch (error) {
      console.error("Error parsing auth data:", error);
    }

    return url;
  };

  const fetchContracts = useCallback(
    async (
      page: number = 1,
      limit: number = 12,
      filterParams: Record<string, any> = {}
    ) => {
      if (!selectedTenantId) return;

      setLoading(true);
      setError(null);
      try {
        const authHeader = localStorage.getItem("authCred") || "";

        const response = await fetch(
          getContractsApiUrl(page, limit, filterParams),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch contracts");
        const data = await response.json();

        const contractsArray = Array.isArray(data.results)
          ? data.results
          : Array.isArray(data)
            ? data
            : [];

        setContracts(contractsArray);
        setContractsCount(
          typeof data.count === "number" ? data.count : contractsArray.length
        );

        const totalCount =
          typeof data.count === "number" ? data.count : contractsArray.length;
        setTotalPages(Math.ceil(totalCount / limit));

        setBackgroundLoading(true);
      } catch (err) {
        console.error("Error fetching contracts:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch contracts"
        );
      } finally {
        setLoading(false);
      }
    },
    [selectedTenantId]
  );

  const handlePageChange = useCallback(
    (page: number, size?: number) => {
      const newPageSize = size || pageSize;
      setCurrentPage(page);
      if (size) setPageSize(newPageSize);

      setStars({});

      fetchContracts(page, newPageSize, filters);
    },
    [pageSize, filters]
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
    },
    [filters]
  );

  useEffect(() => {
    if (selectedTenantId) {
      fetchContracts(1, pageSize);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (backgroundLoading) {
      const fetchBackgroundData = async () => {
        try {
          const teamList = await ContractService.getTeams(
            Number(selectedTenantId)
          );
          setTeams(teamList || []);
        } catch (e) {
          console.error("Background API error:", e);
        } finally {
          setBackgroundLoading(false);
        }
      };

      fetchBackgroundData();
    }
  }, [backgroundLoading, selectedTenantId]);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit");
    if (!editOffcanvas) return;

    const handleHidden = () => {
      setSelectedContract(null);
    };

    editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    return () => {
      editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
    };
  }, []);

  useEffect(() => {
    const addOffcanvas = document.getElementById("offcanvas_add_contract");

    const handleHidden = () => {
      setIsAddContractOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const closeOffcanvas = (offcanvasId: string) => {
    const offcanvas = document.getElementById(offcanvasId);
    if (offcanvas) {
      try {
        if ((window as any).bootstrap?.Offcanvas) {
          const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
            offcanvas
          );
          if (bsOffcanvas) {
            bsOffcanvas.hide();
          } else {
            const newOffcanvas = new (window as any).bootstrap.Offcanvas(
              offcanvas
            );
            newOffcanvas.hide();
          }
        } else {
          const closeButton = offcanvas.querySelector(
            '[data-bs-dismiss="offcanvas"]'
          ) as HTMLElement;
          if (closeButton) {
            closeButton.click();
          } else {
            offcanvas.classList.remove("show");
            const backdrop = document.querySelector(".offcanvas-backdrop");
            if (backdrop) backdrop.remove();
            document.body.classList.remove("offcanvas-open");
            document.body.style.removeProperty("overflow");
            document.body.style.removeProperty("padding-right");
          }
        }
      } catch (error) {
        console.warn("Error closing offcanvas:", error);
      }
    }
  };

  useEffect(() => {
    saveColumnVisibilityToStorage(columnVisibility);
  }, [columnVisibility, saveColumnVisibilityToStorage]);

  const handleAddContract = async (contractData: Partial<ContractRequest>) => {
    setAddContractLoading(true);
    setAddContractError(null);

    try {
      const createdContract = await ContractService.createContract(
        contractData
      );

      setTimeout(() => {
        setResetFormTrigger((prev) => prev + 1);
      }, 100);

      closeOffcanvas("offcanvas_add_contract");

      setTimeout(() => {
        showSuccessAlert(`${getLifecycleTypeName("CONTRACT")} created successfully!`);
      }, 500);

      return createdContract;
    } catch (error: any) {
      console.error("Error creating contract:", error);
      setAddContractError(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}`));
      showErrorAlert(getApiErrorMessage(error, `Failed to create ${getLifecycleTypeName("CONTRACT").toLowerCase()}`));
      throw error;
    } finally {
      setAddContractLoading(false);
    }
  };

  const handleEditContract = async (contractData: Partial<ContractRequest>) => {
    if (!selectedContract) return;

    setEditContractLoading(true);
    setEditContractError(null);

    try {
      await ContractService.updateContract(
        selectedContract.contract_id,
        contractData
      );

      fetchContracts(currentPage, pageSize, filters);
      closeOffcanvas("offcanvas_edit");
      setSelectedContract(null);

      setTimeout(() => {
        showSuccessAlert(`${getLifecycleTypeName("CONTRACT")} updated successfully!`);
      }, 500);
    } catch (error: any) {
      console.error("Error updating contract:", error);
      setEditContractError(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("CONTRACT").toLowerCase()}`));
      showErrorAlert(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("CONTRACT").toLowerCase()}`));
      throw error;
    } finally {
      setEditContractLoading(false);
    }
  };

  const handleEditClick = (contract: any) => {
    flushSync(() => {
      setSelectedContract(contract);
      setEditContractError(null);
    });
  };



  const handleDeleteContract = async (contract: any) => {
    if (!selectedTenantId) return;

    const result = await showConfirmationAlert(
      `Delete ${getLifecycleTypeName("CONTRACT")}`,
      `Are you sure you want to delete "${contract.subject}"? This action cannot be undone.`,
      async () => {
        await ContractService.deleteContract(
          contract.contract_id,
          Number(selectedTenantId)
        );
      }
    );

    if (result.isConfirmed) {
      await showSuccessAlert(`${getLifecycleTypeName("CONTRACT")} deleted successfully!`);
      fetchContracts(currentPage, pageSize, filters);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIndices = Object.keys(stars)
      .filter((key) => stars[parseInt(key)])
      .map((key) => parseInt(key));
    if (selectedIndices.length === 0) return;

    const result = await showConfirmationAlert(
      "Delete Selected Records",
      `Are you sure you want to delete ${selectedIndices.length} selected record${selectedIndices.length > 1 ? "s" : ""}? This action cannot be undone.`,
      async () => {
        setIsBulkDeleting(true);
        try {
          const selectedContracts = selectedIndices
            .map((index) => sortedContracts[index])
            .filter((contract) => contract);

          const deletePromises = selectedContracts.map((contract) =>
            ContractService.deleteContract(
              contract.contract_id,
              Number(selectedTenantId)
            )
          );
          await Promise.all(deletePromises);
        } finally {
          setIsBulkDeleting(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      setStars({});

      await showSuccessAlert(
        `${selectedIndices.length} ${getLifecycleTypeName(
          "CONTRACT"
        ).toLowerCase()}${selectedIndices.length > 1 ? "s" : ""
        } deleted successfully!`
      );

      fetchContracts(currentPage, pageSize, filters);
    }
  };;

  const handlePreviewClick = (contract: any) => {
    window.location.href = `/contracts/${contract.contract_id}`;
  };

  const initializeStarsState = () => {
    const starsState: { [key: number]: boolean } = {};
    contracts.forEach((item, index) => {
      starsState[index] = false;
    });
    setStars(starsState);
  };

  React.useEffect(() => {
    initializeStarsState();
  }, [contracts]);

  const handleStarToggle = (index: number) => {
    setStars((prevStars) => ({
      ...prevStars,
      [index]: !prevStars[index],
    }));
  };

  React.useEffect(() => {
    setStars({});
  }, [currentPage, filters.sortBy, filters.sortOrder]);

  const sortedContracts = useMemo(
    () =>
      sortRecords<any>(contracts, filters.sortBy, filters.sortOrder, {
        assigned_team: (record) =>
          record.assigned_team?.team_name ||
          teams.find((t) => t.team_id === record.assigned_team_id)?.team_name,
      }),
    [contracts, filters.sortBy, filters.sortOrder, teams]
  );

  const userOptions = tenantUsers.map((u) => ({
    value: u.user_id,
    label: `${u.user_first_name.trim()} ${u.user_last_name.trim()}`,
  }));

  const allAvailableColumns = useMemo(
    () => [
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("CONTRACT")} Subject`}
              field="subject"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "subject",
        key: "subject",
        render: (text: string, record: any) => (
          <div>
            <div>
              <Link
                to={`/contracts/${record.contract_id}`}
                state={{ contractData: record }}
                className="title-name"
              >
                {record.subject?.length > 40 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${record.contract_id}`}>{record.subject}</Tooltip>}
                  >
                    <span>{`${record.subject.substring(0, 40)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.subject
                )}
              </Link>
            </div>
            <span className="text-muted small">
              {record.company?.company_name || "-"}
            </span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Company"
              field="company"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "company",
        key: "company",
        render: (company: any, record: any) => (
          <div className={!record.company?.company_name ? "text-center fw-bold" : ""}>
            {record.company?.company_name || "-"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("CONTRACT")} Type`}
              field="contract_type"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "contract_type",
        key: "contract_type",
        render: (text: string, record: any) => (
          <span
            className={`badge badge-pill badge-status ${getStageColor(
              record.contract_type?.contract_type_display_name || "",
              record.contract_type?.contract_type_id,
              record.contract_type?.contract_type_order
            )} no-border`}
            style={{ border: "none", borderColor: "transparent" }}
          >
            {record.contract_type?.contract_type_display_name || "-"}
          </span>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Amount"
              field="grand_total"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "amount",
        key: "amount",
        render: (text: string, record: any) => (
          <>
            {record.grand_total ? (
              <span className="text-dark fw-semibold">
                {record.currency_code || "USD"}{" "}
                {Number(record.grand_total || 0).toFixed(4)}
              </span>
            ) : (
              <div className="text-center fw-bold">-</div>
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned Team"
              field="assigned_team"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_team",
        key: "assigned_team",
        render: (text: string, record: any) => (
          <>
            {record.assigned_team?.team_name
              ? record.assigned_team.team_name
              : (() => {
                const team = teams.find(
                  (t) => t.team_id === record.assigned_team_id
                );
                return team ? team.team_name : "No Team Assigned";
              })()}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned User"
              field="assigned_user"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_user",
        key: "assigned_user",
        render: (text: string, record: any) => (
          <>
            {record.assigned_user ? (
              <div className="d-flex align-items-center">
                {record.assigned_user.avatar_url ? (
                  <img
                    src={record.assigned_user.avatar_url}
                    alt={record.assigned_user.full_name}
                    className="me-2 rounded-circle"
                    style={{ width: 24, height: 24, objectFit: "cover" }}
                  />
                ) : (
                  <img
                    src={male}
                    alt="Default Avatar"
                    className="me-2 rounded-circle"
                    style={{ width: 24, height: 24, objectFit: "cover" }}
                  />
                )}
                <span>{record.assigned_user.full_name}</span>
              </div>
            ) : (
              "No User Assigned"
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`Related ${getLifecycleTypeName("PROPOSAL")}`}
              field="proposal"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "related_proposal",
        key: "related_proposal",
        render: (text: string, record: any) => (
          <>
            {record.proposal?.proposal_id ? (
              <Link
                to={`/proposals/${record.proposal.proposal_id}`}
                className="crm-link"
                target="_blank"
              >
                {record.proposal.subject?.length > 30 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-proposal-${record.proposal.proposal_id}`}>{record.proposal.subject}</Tooltip>}
                  >
                    <span>{`${record.proposal.subject.substring(0, 30)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.proposal.subject || "-"
                )}
              </Link>
            ) : record.proposal?.subject?.length > 30 ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tooltip-proposal-subject-${record.contract_id}`}>{record.proposal.subject}</Tooltip>}
              >
                <span>{`${record.proposal.subject.substring(0, 30)}...`}</span>
              </OverlayTrigger>
            ) : (
              record.proposal?.subject || "-"
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Created"
              field="created_datetime"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "created_datetime",
        key: "created_datetime",
        render: (text: string, record: any) => (
          <>
            {record.created_datetime ? (
              formatCustomDateTime(record.created_datetime)
            ) : (
              "-"
            )}
          </>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Currency"
            field="currency_code"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "currency_code",
        key: "currency_code",
        render: (text: string, record: any) => (
          <div className={!record.currency_code ? "text-center fw-bold" : ""}>{record.currency_code || "-"}</div>
        ),
      },
    ],
    [filters.sortBy, filters.sortOrder, handleSortChange, teams]
  );

  const buildContractSections = useCallback(
    (contract: any): EntityDetailSection[] => {
      return [
        {
          title: `${getLifecycleTypeName("CONTRACT")} Info`,
          icon: "ti ti-file-certificate",
          fields: [
            { label: "Subject", value: contract.subject, fullWidth: true },
            {
              label: `${getLifecycleTypeName("CONTRACT")} Type`,
              value: contract.contract_type?.contract_type_display_name,
            },
          ],
        },
        {
          title: `Company & Related ${getLifecycleTypeName("PROPOSAL")}`,
          icon: "ti ti-building",
          fields: [
            { label: "Company", value: contract.company?.company_name },
            {
              label: `Related ${getLifecycleTypeName("PROPOSAL")}`,
              value: contract.proposal?.proposal_id ? (
                <EntityDetailLink
                  href={`/proposals/${contract.proposal.proposal_id}`}
                  label={contract.proposal.subject}
                />
              ) : (
                contract.proposal?.subject
              ),
            },
          ],
        },
        {
          title: "Ownership",
          icon: "ti ti-users",
          fields: [
            {
              label: "Assigned Team",
              value: contract.assigned_team?.team_name,
            },
            {
              label: "Assigned User",
              value: contract.assigned_user?.full_name,
            },
          ],
        },
        {
          title: "Financials",
          icon: "ti ti-currency-dollar",
          fields: [
            {
              label: "Amount",
              value: contract.grand_total
                ? `${contract.currency_code || "USD"} ${Number(
                  contract.grand_total || 0
                ).toFixed(4)}`
                : null,
            },
            { label: "Currency", value: contract.currency_code },
          ],
        },
        {
          title: "Timestamps",
          icon: "ti ti-clock",
          fields: [
            {
              label: "Created",
              value: contract.created_datetime
                ? formatCustomDateTime(contract.created_datetime)
                : null,
            },
            {
              label: "Updated",
              value: contract.updated_datetime
                ? formatCustomDateTime(contract.updated_datetime)
                : null,
            },
          ],
        },
      ];
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              checked={
                !!sortedContracts.every((_: any, idx: number) => stars[idx])
              }
              onChange={(e) => {
                const checked = e.target.checked;
                setStars((prev) => {
                  const newStars = { ...prev };
                  sortedContracts.forEach((_: any, idx: number) => {
                    newStars[idx] = checked;
                  });
                  return newStars;
                });
              }}
            />
          </div>
        ),
        dataIndex: "selection",
        render: (_: any, record: any, index: number) => (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              checked={!!stars[index]}
              onChange={() => handleStarToggle(index)}
            />
          </div>
        ),
      },
      ...getSortedAvailableColumns()
        .filter((col) => columnVisibility[col.key])
        .map((col) =>
          allAvailableColumns.find((allCol) => allCol.key === col.key)
        )
        .filter(Boolean),
      {
        title: "Actions",
        dataIndex: "actions",
        render: (text: string, record: any) => (
          <div className="d-flex align-items-center justify-content-end gap-2 position-static">
            <Link
              to="#"
              className="action-icon"
              onClick={(e) => {
                e.preventDefault();
                setDetailsContract(record);
              }}
            >
              <i className="ti ti-eye text-blue-light" />
            </Link>
            <div className="dropdown table-action position-static">
              <Link
                to="#"
                className="action-icon"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
              >
                <i className="fa fa-ellipsis-v" />
              </Link>
              <div className="dropdown-menu dropdown-menu-end">
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePreviewClick(record);
                  }}
                >
                  <i className="ti ti-info-circle text-blue-light" /> View Details
                </Link>
                <Link
                  className="dropdown-item edit-popup"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_edit"
                  onClick={() => handleEditClick(record)}
                >
                  <i className="ti ti-edit text-blue" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteContract(record);
                  }}
                >
                  <i className="ti ti-trash text-danger" /> Delete
                </Link>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [
      columnVisibility,
      contracts,
      stars,
      setStars,
      handleStarToggle,
      getSortedAvailableColumns,
      allAvailableColumns,
      handleEditClick,
      handleDeleteContract,
      handlePreviewClick,
    ]
  );

  return (
    <ContractFormDataProvider isMainApiCompleted={!loading} shouldLoad={isAddContractOpen || !!selectedContract}>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      {getLifecycleTypePluralName("CONTRACT")}
                      <span className="count-title">{contractsCount || 0}</span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <ListFilterChips
                        entityType="contract"
                        filters={filters}
                        onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                        onClearAll={() => applyListFilters(getClearedListFilters())}
                        disabled={loading}
                        className="mb-0"
                      />
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        {Object.values(stars).some(Boolean) && (
                          <button
                            type="button"
                            className="btn btn-danger ms-3"
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                          >
                            <i className="ti ti-trash me-2" />
                            Delete Selected (
                            {Object.values(stars).filter(Boolean).length})
                          </button>
                        )}{" "}
                        &nbsp; &nbsp;
                        <ExportComponent entityType="contract" filters={filters} />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add_contract"
                          onClick={() => setIsAddContractOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add {getLifecycleTypeName("CONTRACT")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={contracts}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <SortByDropdown
                      value={filters.sort_by}
                      onChange={(sortBy) =>
                        setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                      }
                    />
                    <span className="mx-2">
                      <LifecycleFilterDropdown
                        entityType="contract"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={{ ...filters }}
                        loading={loading}
                      />
                    </span>
                    <ManageColumns
                      availableColumns={availableColumns}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                      onResetColumns={handleResetColumns}
                      onReorderColumns={setOrderedAvailableColumns}
                    />
                    <div className="view-icons ms-3">
                      <Link
                        to="#"
                        className={currentView === "list" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewSwitch("list");
                        }}
                      >
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        className={currentView === "grid" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewSwitch("grid");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>
                {loading ? (
                  <div className="card-body">
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: "60vh" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading {getLifecycleTypePluralName("CONTRACT")}...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : currentView === "grid" ? (
                  <ContractsGrid
                    contracts={contracts}
                    loading={loading}
                    error={error}
                    currentView={currentView}
                    onViewSwitch={handleViewSwitch}
                    pagination={{
                      currentPage: currentPage,
                      totalCount: contractsCount || 0,
                      pageSize: pageSize,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(current: number, size: number) => {
                      setCurrentPage(1);
                      setPageSize(size);
                      setStars({});
                      fetchContracts(1, size, filters);
                    }}
                    onAddContract={() => setIsAddContractOpen(true)}
                  />
                ) : (
                  <div className="card-body">
                    <div
                      className="table-responsive contact-table-wrapper"
                      style={{ position: "relative" }}
                    >
                      {isFilterLoading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            minHeight: "200px",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <span>Filtering results...</span>
                          </div>
                        </div>
                      )}
                      {contracts.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                            <i className="ti ti-file-certificate fs-36 text-gray"></i>
                          </div>
                          <h4 className="text-gray mb-2">
                            No {getLifecycleTypePluralName("CONTRACT")} Found!
                          </h4>
                          <p className="text-muted mb-3">
                            There are no {getLifecycleTypePluralName("CONTRACT").toLowerCase()} to display at the moment.
                          </p>
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_contract"
                            onClick={() => setIsAddContractOpen(true)}
                          >
                            <i className="fa fa-plus me-2" />
                            Add Your First {getLifecycleTypeName("CONTRACT")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <Table
                            columns={columns}
                            dataSource={sortedContracts}
                            rowKey={(record: any) =>
                              record.contract_id || record.id
                            }
                            rowSelection={false}
                            className="table table-striped contact-table"
                            pagination={false}
                          />
                          <TablePagination
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalItems={contractsCount || 0}
                            filteredItems={contractsCount || 0}
                            isFiltered={
                              filters.searchTerm !== "" ||
                              filters.dateRange[0] !== null ||
                              filters.dateRange[1] !== null
                            }
                            onPageChange={handlePageChange}
                            onPageSizeChange={(
                              current: number,
                              size: number
                            ) => {
                              setCurrentPage(1);
                              setPageSize(size);
                              setStars({});
                              fetchContracts(1, size, filters);
                            }}
                            entityName={getLifecycleTypePluralName("CONTRACT")}
                            loading={loading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Add New Contracts Off-canvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_add_contract"
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Add New {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {addContractError && (
            <div className="alert alert-danger" role="alert">
              {addContractError}
            </div>
          )}
          {isAddContractOpen && (
            <ContractForm
              key={`create-new-contract-${resetFormTrigger}`}
              onSubmit={handleAddContract}
              isSubmitting={addContractLoading}
              resetTrigger={resetFormTrigger}
              onRefreshList={() => fetchContracts(currentPage, pageSize, filters)}
              onCancel={() => {
                const offcanvas = document.getElementById(
                  "offcanvas_add_contract"
                );
                if (offcanvas) {
                  try {
                    if ((window as any).bootstrap?.Offcanvas) {
                      const bsOffcanvas = (
                        window as any
                      ).bootstrap.Offcanvas.getInstance(offcanvas);
                      if (bsOffcanvas) {
                        bsOffcanvas.hide();
                      }
                    } else {
                      const closeButton = offcanvas.querySelector(
                        '[data-bs-dismiss="offcanvas"]'
                      ) as HTMLElement;
                      if (closeButton) {
                        closeButton.click();
                      }
                    }
                  } catch (error) {
                    console.warn("Error closing add offcanvas:", error);
                    const closeButton = offcanvas.querySelector(
                      '[data-bs-dismiss="offcanvas"]'
                    ) as HTMLElement;
                    if (closeButton) {
                      closeButton.click();
                    }
                  }
                }
              }}
            />
          )}
        </div>
      </div>
      {/* /Add New Contracts Off-canvas */}

      {/* Edit Contracts */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit"
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {editContractError && (
            <div className="alert alert-danger" role="alert">
              {editContractError}
            </div>
          )}
          {selectedContract && (
            <ContractFormDataProvider isMainApiCompleted={true}>
              <ContractForm
                contract={selectedContract}
                onSubmit={handleEditContract}
                isSubmitting={editContractLoading}
                onCancel={() => closeOffcanvas("offcanvas_edit")}
                isMainApiCompleted={true}
              />
            </ContractFormDataProvider>
          )}
        </div>
      </div>
      {/* /Edit Contracts */}

      {/* Delete Contract Modal Removed */}

      {/* Bulk Delete Confirmation Modal */}

      {detailsContract && (
        <EntityDetailsModal
          isOpen={!!detailsContract}
          onClose={() => setDetailsContract(null)}
          title={detailsContract.subject}
          subtitle={detailsContract.company?.company_name || undefined}
          eyebrow={getLifecycleTypeName("CONTRACT")}
          avatarIcon="ti ti-file-certificate"
          badge={{
            label:
              detailsContract.contract_type?.contract_type_display_name ||
              "No Type",
            tone: "info",
          }}
          sections={buildContractSections(detailsContract)}
          onEdit={() => {
            const contract = detailsContract;
            setDetailsContract(null);
            handleEditClick(contract);
            openOffcanvas("offcanvas_edit");
          }}
        />
      )}

    </ContractFormDataProvider>
  );
};

export default Contracts;
